import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Calendar, BookOpen, CalendarDays, HandHeart, LogOut,
  ArrowRight, Clock, CheckCircle2, Loader2, MapPin,
  Sparkles, Star, Users
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import BookingCalendar from '../../components/booking/BookingCalendar';
import RegisterDialog from '../../components/RegisterDialog';
import { useSiteContent } from '../../context/SiteContentContext';
import { DEFAULT_LOGO } from '../../siteContent';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return ''; }
};

const memberSinceLabel = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }); }
  catch { return ''; }
};

const STATUS_STYLES = {
  scheduled:  'bg-blue-50  text-blue-700  border-blue-200',
  completed:  'bg-[#7CA982]/10 text-[#2d6a31] border-[#7CA982]/30',
  cancelled:  'bg-red-50   text-red-600   border-red-200',
  pending:    'bg-amber-50 text-amber-700 border-amber-200',
};

/* ── Shared card sub-components ─────────────────────────────── */

const ArticleCard = ({ a }) => (
  <Link
    to={`/knowledge-hub/${a.id}`}
    className="bg-white rounded-3xl border border-[#EAE5E0] overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
    data-testid="dashboard-article-card"
  >
    <div className="h-40 bg-[#003366]/5 flex-shrink-0 overflow-hidden">
      {a.cover_image
        ? <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
        : <div className="w-full h-full flex items-center justify-center"><BookOpen className="w-10 h-10 text-[#003366]/20" /></div>
      }
    </div>
    <div className="p-5 flex flex-col flex-1">
      <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#E74C3C] bg-[#E74C3C]/10 rounded-full px-2.5 py-0.5 mb-3 self-start">{a.category}</span>
      <h3 className="font-heading text-base font-bold text-[#1A1A1A] leading-snug line-clamp-2">{a.title}</h3>
      <p className="text-sm text-[#666] mt-2 line-clamp-2 flex-1">{a.excerpt}</p>
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#EAE5E0]">
        <span className="text-xs text-[#999]">{formatDate(a.created_at)}</span>
        <span className="text-xs text-[#999]">3 min read</span>
      </div>
      <div className="mt-3 text-sm font-semibold text-[#003366] flex items-center gap-1.5">
        Read article <ArrowRight className="w-4 h-4" />
      </div>
    </div>
  </Link>
);

const EventCard = ({ e }) => {
  const parts = (e.event_date || '').split(' ');
  const day = parts[0] || '';
  const month = parts[1] || '';
  return (
    <div
      className="bg-white rounded-3xl border border-[#EAE5E0] overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
      data-testid="dashboard-event-card"
    >
      <div className="h-32 bg-[#003366] flex flex-col items-center justify-center flex-shrink-0 relative overflow-hidden">
        <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-[#E74C3C]/20 blur-xl" />
        <span className="font-heading font-bold text-4xl text-white leading-none relative">{day}</span>
        <span className="text-xs uppercase tracking-widest text-white/60 mt-1 relative">{month}</span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#E74C3C] bg-[#E74C3C]/10 rounded-full px-2.5 py-0.5">{e.category}</span>
          <span className="text-xs text-[#999]">·</span>
          <span className="text-xs text-[#666]">{e.format}</span>
        </div>
        <h3 className="font-heading text-base font-bold text-[#1A1A1A] leading-snug line-clamp-2">{e.title}</h3>
        {e.description && <p className="text-sm text-[#666] mt-2 line-clamp-2 flex-1">{e.description}</p>}
        {e.location && (
          <span className="text-xs text-[#999] flex items-center gap-1 mt-2"><MapPin className="w-3 h-3" />{e.location}</span>
        )}
        <div className="mt-4">
          <RegisterDialog
            kind="event"
            refId={e.id}
            title={e.title}
            label="Register — Free"
            testid="dashboard-event-register"
            triggerClassName="w-full rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white font-medium"
          />
        </div>
      </div>
    </div>
  );
};

/* ── Main component ──────────────────────────────────────────── */

const MemberDashboard = () => {
  const navigate = useNavigate();
  const { home } = useSiteContent();
  const logo = home?.logo || DEFAULT_LOGO;

  const [user, setUser] = useState(null);
  const [member, setMember] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [articles, setArticles] = useState([]);
  const [events, setEvents] = useState([]);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchAll(token);
  }, []);

  const fetchAll = async (token) => {
    try {
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      const [mRes, bRes, aRes, eRes] = await Promise.all([
        axios.get(`${API}/members/me`, cfg),
        axios.get(`${API}/bookings/`, cfg),
        axios.get(`${API}/articles/`),
        axios.get(`${API}/events/`),
      ]);
      setMember(mRes.data);
      setBookings(bRes.data || []);
      setArticles(aRes.data || []);
      setEvents(eRes.data || []);
    } catch (e) {
      console.error('Dashboard fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleBookingCreated = (newBooking) => {
    setBookings([newBooking, ...bookings]);
    setShowBookingForm(false);
    const token = localStorage.getItem('token');
    if (token) fetchAll(token);
  };

  const firstName = user?.full_name?.split(' ')[0] || 'there';
  const scheduledCount = bookings.filter((b) => b.status === 'scheduled').length;
  const planLabel = (member?.membership_plan || 'Founding Member').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const stats = [
    { label: 'Upcoming Sessions', value: scheduledCount, Icon: Calendar,     accent: '#E74C3C' },
    { label: 'Events Open',        value: events.length,  Icon: CalendarDays, accent: '#003366' },
    { label: 'Articles Available', value: articles.length,Icon: BookOpen,     accent: '#7CA982' },
    { label: 'Membership',         value: 'Active',       Icon: CheckCircle2, accent: '#003366' },
  ];

  const actions = [
    { Icon: Calendar,     label: 'Book a Session',         desc: 'Request 1-on-1 guidance from an expert', onClick: () => { setShowBookingForm(true); setTimeout(() => document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' }), 50); }, testid: 'action-book-session', accent: '#E74C3C' },
    { Icon: CalendarDays, label: 'Browse Events',           desc: 'Free community sessions every month',    href: '/events',                testid: 'action-events',       accent: '#003366' },
    { Icon: BookOpen,     label: 'Knowledge Hub',           desc: 'Articles, guides and resources',         href: '/knowledge-hub',         testid: 'action-knowledge-hub',accent: '#003366' },
    { Icon: HandHeart,    label: 'Corporate Life Series',   desc: 'Our structured monthly sessions',         href: '/corporate-life-series', testid: 'action-cls',          accent: '#E74C3C' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#003366] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF9]">

      {/* ── Top nav ───────────────────────────────────────────── */}
      <nav className="bg-white border-b border-[#EAE5E0] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="dashboard-logo-link">
            <img src={logo} alt="Equaltoo Foundation" className="h-8 w-auto" />
          </Link>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-2 text-sm text-[#666] hover:text-[#003366] transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </nav>

      {/* ── Hero banner ───────────────────────────────────────── */}
      <div className="bg-[#003366] text-white relative overflow-hidden">
        <div className="absolute -top-24 -right-20 w-80 h-80 rounded-full bg-[#E74C3C]/15 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 w-56 h-56 rounded-full bg-white/5 blur-2xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 relative">
          <p className="text-white/50 text-xs font-semibold uppercase tracking-[0.2em] mb-2">{getGreeting()}</p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold" data-testid="member-greeting">
            {firstName}
          </h1>
          <p className="text-white/70 mt-2 text-base max-w-xl">
            Welcome back to your Equaltoo member portal — everything you need is here.
          </p>

          <div className="flex flex-wrap items-center gap-3 mt-6">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-[#E74C3C] px-4 py-1.5 text-sm font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              {planLabel}
            </div>
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 border border-white/20 px-3 py-1.5 text-sm">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#7CA982]" />
              <span className="capitalize">{member?.membership_status || 'Active'}</span>
            </div>
            {member?.start_date && (
              <span className="text-sm text-white/45">Member since {memberSinceLabel(member.start_date)}</span>
            )}
          </div>
        </div>
      </div>

      {/* ── Stats row ─────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 py-6">
          {stats.map(({ label, value, Icon, accent }) => (
            <div key={label} className="bg-white rounded-2xl border border-[#EAE5E0] p-5 flex items-center gap-4 shadow-sm" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: `${accent}18` }}>
                <Icon className="w-5 h-5" style={{ color: accent }} />
              </div>
              <div className="min-w-0">
                <p className="font-heading font-bold text-[#1A1A1A] text-xl leading-none truncate">{value}</p>
                <p className="text-xs text-[#888] mt-1 leading-tight">{label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Main content ──────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-20 space-y-14">

        {/* Quick actions */}
        <section>
          <h2 className="font-heading text-xl font-bold text-[#1A1A1A] mb-5">What would you like to do?</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {actions.map((a) => {
              const Icon = a.Icon;
              const inner = (
                <div
                  className="bg-white rounded-3xl border border-[#EAE5E0] p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group h-full"
                  data-testid={a.testid}
                  onClick={a.onClick}
                >
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: `${a.accent}12` }}>
                    <Icon className="w-6 h-6" style={{ color: a.accent }} />
                  </div>
                  <p className="font-heading font-bold text-[#1A1A1A] text-base leading-tight">{a.label}</p>
                  <p className="text-sm text-[#888] mt-1.5 leading-snug">{a.desc}</p>
                  <div className="mt-5 flex items-center gap-1 text-xs font-semibold text-[#ccc] group-hover:text-[#003366] transition-colors">
                    Go <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              );
              return a.href
                ? <Link key={a.label} to={a.href} className="block">{inner}</Link>
                : <div key={a.label}>{inner}</div>;
            })}
          </div>
        </section>

        {/* Booking form */}
        {showBookingForm && (
          <section id="booking-section" className="scroll-mt-6">
            <div className="bg-white rounded-3xl border border-[#EAE5E0] p-6 lg:p-8 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold text-[#1A1A1A]">Book a Session</h2>
                <button onClick={() => setShowBookingForm(false)} className="text-sm text-[#888] hover:text-[#003366] transition-colors" data-testid="close-booking-form">Cancel</button>
              </div>
              <BookingCalendar onBookingCreated={handleBookingCreated} />
            </div>
          </section>
        )}

        {/* Recommended Articles */}
        {articles.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-xl font-bold text-[#1A1A1A]">Recommended Articles</h2>
                <p className="text-sm text-[#888] mt-0.5">Curated resources for your professional journey</p>
              </div>
              <Link to="/knowledge-hub" className="hidden sm:flex items-center gap-1 text-sm font-semibold text-[#003366] hover:text-[#E74C3C] transition-colors" data-testid="view-all-articles">
                View all <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.slice(0, 3).map((a) => <ArticleCard key={a.id} a={a} />)}
            </div>
            <Link to="/knowledge-hub" className="sm:hidden mt-4 flex items-center gap-1 text-sm font-semibold text-[#003366]">
              View all articles <ArrowRight className="w-4 h-4" />
            </Link>
          </section>
        )}

        {/* Upcoming Events */}
        {events.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-xl font-bold text-[#1A1A1A]">Upcoming Events</h2>
                <p className="text-sm text-[#888] mt-0.5">Register your spot — all events are free</p>
              </div>
              <Link to="/events" className="hidden sm:flex items-center gap-1 text-sm font-semibold text-[#003366] hover:text-[#E74C3C] transition-colors" data-testid="view-all-events">
                View all <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.slice(0, 3).map((e) => <EventCard key={e.id} e={e} />)}
            </div>
            <Link to="/events" className="sm:hidden mt-4 flex items-center gap-1 text-sm font-semibold text-[#003366]">
              View all events <ArrowRight className="w-4 h-4" />
            </Link>
          </section>
        )}

        {/* My Appointments */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-heading text-xl font-bold text-[#1A1A1A]">My Appointments</h2>
              <p className="text-sm text-[#888] mt-0.5">Your booked and past guidance sessions</p>
            </div>
            {bookings.length > 0 && (
              <span className="text-sm text-[#888]">{bookings.length} total</span>
            )}
          </div>

          {bookings.length === 0 ? (
            <div className="bg-white rounded-3xl border border-[#EAE5E0] p-12 text-center shadow-sm" data-testid="no-bookings">
              <div className="w-16 h-16 rounded-2xl bg-[#003366]/5 flex items-center justify-center mx-auto mb-5">
                <Calendar className="w-8 h-8 text-[#003366]/30" />
              </div>
              <p className="font-heading font-semibold text-[#1A1A1A] text-lg">No appointments yet</p>
              <p className="text-sm text-[#888] mt-2 max-w-xs mx-auto">Book your first 1-on-1 guidance session with an Equaltoo expert.</p>
              <button
                onClick={() => { setShowBookingForm(true); setTimeout(() => document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' }), 50); }}
                data-testid="empty-state-book-btn"
                className="mt-6 rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white px-6 py-2.5 text-sm font-semibold transition-colors"
              >
                Book a Session
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {bookings.map((b) => (
                <div key={b.id} className="bg-white rounded-2xl border border-[#EAE5E0] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 shadow-sm hover:shadow-md transition-shadow" data-testid="booking-card">
                  {/* Date block */}
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-[#003366] flex flex-col items-center justify-center text-white hidden sm:flex">
                    <span className="font-heading font-bold text-lg leading-none">
                      {new Date(b.appointment_date).getDate()}
                    </span>
                    <span className="text-[10px] uppercase tracking-wide text-white/60 mt-0.5">
                      {new Date(b.appointment_date).toLocaleDateString('en-IN', { month: 'short' })}
                    </span>
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <p className="font-heading font-semibold text-[#1A1A1A] capitalize">{b.service_type} Session</p>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border capitalize ${STATUS_STYLES[b.status] || STATUS_STYLES.pending}`} data-testid="booking-status-chip">
                        {b.status}
                      </span>
                    </div>
                    <p className="text-sm text-[#666]">
                      {new Date(b.appointment_date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
                      {b.appointment_time ? ` · ${b.appointment_time}` : ''}
                    </p>
                    {b.professional_name && (
                      <p className="text-sm text-[#888] mt-0.5">With {b.professional_name}</p>
                    )}
                    {b.notes && (
                      <p className="text-sm text-[#888] mt-0.5 italic truncate max-w-md">{b.notes}</p>
                    )}
                  </div>

                  {/* Meeting link */}
                  {b.meeting_link && (
                    <a
                      href={b.meeting_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#003366] hover:bg-[#002244] text-white px-4 py-2 text-sm font-medium transition-colors"
                      data-testid="join-meeting-link"
                    >
                      Join <ArrowRight className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

      </div>
    </div>
  );
};

export default MemberDashboard;
